import { fg } from '@atlaskit/platform-feature-flags/fg';
import { getRovoParams, updatePageRovoParams } from '@atlaskit/rovo-triggers/params';
import { EMPTY_CONVERSATION_ID } from '@atlassian/conversation-assistant-store/controllers/chat';

import { persistRecentConversation } from '../recent-conversation-persistence/recentConversationPersistence';
import { getConversationHydrationCoordinator } from './conversationHydrationCoordinator';
import type { ChatStoreAction } from './types';

export type RemoveConversationFromStoreArgs = {
	conversationId: string;
	/** A successful local delete or an explicit null live session snapshot. */
	confirmedDeletion?: boolean;
	/** Capture before awaiting deletion; omitted by unrelated metadata-cleanup callers. */
	promptHistoryContext?: { requestToken: object; sessionId: string };
};

/** Remove a deleted conversation without issuing another backend delete. */
export const removeConversationFromStore =
	({
		conversationId,
		confirmedDeletion,
		promptHistoryContext,
	}: RemoveConversationFromStoreArgs): ChatStoreAction =>
	({ getState, setState }, props) => {
		const { assistanceService, getConversationHydrationScopeKey, siteId } = props;
		const scopeKey = getConversationHydrationScopeKey?.();
		if (scopeKey != null) {
			getConversationHydrationCoordinator(assistanceService, scopeKey).markRemoved(conversationId);
		}
		const { conversations, promptHistory, currentConversationId } = getState();
		const cleanup = confirmedDeletion && fg('rovo-chat-ai-inbox');
		const { [conversationId]: conversation, ...otherConversations } = conversations;
		let updatedHistory: typeof promptHistory;
		if (
			promptHistory &&
			(cleanup ||
				(promptHistoryContext && promptHistory.requestToken === promptHistoryContext.requestToken))
		) {
			const removedSessionIds = new Set([
				...(promptHistory.removedSessionIds ?? []),
				conversationId,
				...(promptHistoryContext ? [promptHistoryContext.sessionId] : []),
			]);
			updatedHistory = {
				...promptHistory,
				removedSessionIds: [...removedSessionIds],
				items: promptHistory.items.filter(({ sessionId }) => !removedSessionIds.has(sessionId)),
			};
		}
		// Remove metadata and selection together, before abort callbacks can run.
		setState({
			conversations: otherConversations,
			...(cleanup && currentConversationId === conversationId
				? { currentConversationId: EMPTY_CONVERSATION_ID }
				: {}),
			...(updatedHistory ? { promptHistory: updatedHistory } : {}),
		});
		if (!cleanup) return;
		const abort = conversation?.storeAbortController?.abort();
		const clearRuntimeCaches = () => {
			props.endReconnection(conversationId);
			props.setMessages(conversationId, []);
			props.unifiedTasksActions?.removeTasksForConversation?.(conversationId);
		};
		clearRuntimeCaches();
		// Replay can flush buffered messages before acknowledging cancellation.
		void abort?.then(() => {
			if (getConversationHydrationScopeKey?.() === scopeKey) clearRuntimeCaches();
		});
		persistRecentConversation({ type: 'conversation-deleted', siteId, conversationId });
		const urlConversationId = getRovoParams().conversationId;
		if (urlConversationId === conversationId) {
			updatePageRovoParams({ conversationId: undefined }, { historyMode: 'replace' });
		}
	};
